// MediaLibraryPickerConnected — the data-connected wrapper around the presentational
// MediaLibraryPicker (deck states 03/06). DL-048 + Gordon universal-access addendum
// 2026-07-14: this is the ONE reusable connected picker every media-attaching surface
// mounts (via Shared/MediaSelection), so the reuse modal is available everywhere media
// can be called from — never re-implemented per surface (everything-is-a-shared-component).
//
// No fork: reuses the Media Library page's own read (getTotalMedia), normalizer
// (normalizeMediaResponse) and cache KEY, so the modal and the page share one cache
// entry and can never diverge. `enabled: open` means NO media read fires until the
// modal is actually opened — so a flag-OFF surface (never mounted) reads nothing.
import React from "react";
import { useQuery } from "react-query";
import { getTotalMedia } from "../../services/api";
import { normalizeMediaResponse, normalizeMediaTypes } from "../../pages/MediaLibrary/mediaAdapter";
import { MEDIA_LIBRARY_REAL_SHAPE } from "../../pages/MediaLibrary/featureFlag";
import MediaLibraryPicker from "./MediaLibraryPicker";

// Same key the standalone page + LibrarySource used, so all surfaces share cache.
const MEDIA_KEY = ["mediaLibrary", "all"];

export default function MediaLibraryPickerConnected({
  open = false,
  acceptedTypes = null,
  segmentName = null,
  contextLabel = null,
  entryLabel,
  onClose,
  onInsert,
}) {
  const mediaQuery = useQuery(
    MEDIA_KEY,
    async () => {
      const res = await getTotalMedia();
      // EW-1280: the picker shares the page's MEDIA_KEY cache, so it MUST use the
      // same flag/normalizer or the shared cache would diverge. ON → real files
      // from res.data.data.types.{cat}.data[]; OFF → legacy wrong-depth unwrap.
      if (MEDIA_LIBRARY_REAL_SHAPE) {
        const types = res?.data?.data?.types ?? {};
        return normalizeMediaTypes(types);
      }
      const grouped = res?.data?.data?.data?.data ?? res?.data?.data ?? {};
      return normalizeMediaResponse(grouped);
    },
    { enabled: open, staleTime: 60_000 }
  );

  if (!open) return null;

  const status = mediaQuery.isLoading
    ? "loading"
    : mediaQuery.isError
    ? "error"
    : "ready";

  return (
    <MediaLibraryPicker
      open
      items={mediaQuery.data || []}
      status={status}
      acceptedTypes={acceptedTypes}
      segmentName={segmentName}
      contextLabel={contextLabel}
      entryLabel={entryLabel}
      onRetry={() => mediaQuery.refetch()}
      onClose={onClose}
      onInsert={onInsert}
    />
  );
}
